import axios from 'axios';
import request from '../../infra/request';
import { RequestStatus } from '../../model';
import type {
  AssessmentBookingStatusDto,
  AssessmentDraftPayload,
  AssessmentRequestPayload,
  QuestionnairePart,
  QuestionnaireTemplate,
  SubmitQuestionnaireApiResponse,
} from '../types/questionnaire';

const errorCodeFromResponseBody = (body: unknown): string | undefined => {
  if (body === null || typeof body !== 'object') return undefined;
  const raw = (body as Record<string, unknown>).errorCode;
  return typeof raw === 'string' ? raw : undefined;
};

export type CreateAssessmentRequestResult =
  | { status: RequestStatus.SUCCESS; data: unknown }
  | {
    status: RequestStatus.FAILURE;
    error: string;
    errorCode: string | undefined;
    /** HTTP status when the server answered. */
    httpStatus: number | undefined;
    /** The request never got a response (timeout or network failure). */
    networkError: boolean;
  };

export interface QuestionnaireSubmissionPayload {
  assessmentType: 'ADHD';
  /** The workflow of the booking just made here, when this browser knows it. */
  workflowId?: string;
  identity: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  };
  questionnaire: {
    templateId: string;
    templateVersion: number;
    answers: Record<string, unknown>;
    attachments?: { fileId?: string; fileName?: string }[];
  };
}

export type AssessmentDraftResult =
  | { status: RequestStatus.SUCCESS; workflowId: string }
  | { status: RequestStatus.FAILURE; errorCode: string | undefined };

/** The workflow id in a draft or booking response, when there is one. */
export const workflowIdFromResponse = (body: unknown): string | undefined => {
  if (body === null || typeof body !== 'object') return undefined;
  // eslint-disable-next-line no-underscore-dangle
  const embedded = (body as { _embedded?: Partial<AssessmentBookingStatusDto> })._embedded;
  return typeof embedded?.workflowId === 'string' && embedded.workflowId
    ? embedded.workflowId
    : undefined;
};

const sendDraft = async (
  send: () => Promise<{ data: unknown }>,
): Promise<AssessmentDraftResult> => {
  try {
    const { data } = await send();
    const workflowId = workflowIdFromResponse(data);
    return workflowId
      ? { status: RequestStatus.SUCCESS, workflowId }
      : { status: RequestStatus.FAILURE, errorCode: undefined };
  } catch (error) {
    return {
      status: RequestStatus.FAILURE,
      errorCode: axios.isAxiosError(error)
        ? errorCodeFromResponseBody(error.response?.data)
        : undefined,
    };
  }
};

export const createAssessmentDraft = (
  payload: AssessmentDraftPayload,
): Promise<AssessmentDraftResult> => sendDraft(
  () => request.post('/assessment-requests/drafts', payload),
);

export const updateAssessmentDraft = (
  workflowId: string,
  payload: AssessmentDraftPayload,
): Promise<AssessmentDraftResult> => sendDraft(
  () => request.patch(`/assessment-requests/drafts/${encodeURIComponent(workflowId)}`, payload),
);

export type SubmitQuestionnaireResult =
  | { status: RequestStatus.SUCCESS; data: SubmitQuestionnaireApiResponse }
  | { status: RequestStatus.FAILURE; error: string; errorCode: string | undefined };

interface QuestionnaireTemplateApiResponse {
  success?: boolean;
  _embedded?: {
    template: RawQuestionnaireTemplate;
  };
  parts?: QuestionnairePart[];
}

interface RawQuestionnaireTemplate {
  id?: string;
  name?: string;
  title?: string;
  templateId?: string;
  templateVersion?: number;
  instructions?: string;
  estimatedMinutes?: number;
  parts?: QuestionnairePart[];
}

function parseTemplateVersionFromId(id: string | undefined): number {
  if (!id) return 1;
  const match = id.match(/_v(\d+)$/);
  return match ? Number(match[1]) : 1;
}

function normalizeTemplate(raw: RawQuestionnaireTemplate | undefined): QuestionnaireTemplate | null {
  if (!raw) return null;
  const parts = raw.parts ?? [];
  return {
    templateId: raw.templateId ?? raw.id ?? '',
    templateVersion: raw.templateVersion ?? parseTemplateVersionFromId(raw.id ?? raw.templateId),
    name: raw.name ?? '',
    title: raw.title ?? raw.name ?? '',
    instructions: raw.instructions,
    estimatedMinutes: raw.estimatedMinutes,
    parts,
  };
}

export const getQuestionnaireTemplate = async (
  name: string,
  templateVersion?: number,
) => {
  try {
    const { data } = await request.get<QuestionnaireTemplateApiResponse>(
      '/questionnaire-templates',
      { params: { name, ...(templateVersion ? { templateVersion } : {}) } },
    );

    // eslint-disable-next-line dot-notation
    const raw = data['_embedded']?.template ?? (data.parts != null ? (data as unknown as RawQuestionnaireTemplate) : undefined);
    const normalized = normalizeTemplate(raw);

    if (!normalized) {
      return {
        status: RequestStatus.FAILURE,
        error: 'Invalid questionnaire template response',
      };
    }

    return {
      status: RequestStatus.SUCCESS,
      data: normalized,
    };
  } catch (error) {
    return {
      status: RequestStatus.FAILURE,
      error: String(error),
    };
  }
};

export const submitQuestionnaireRequest = async (
  payload: QuestionnaireSubmissionPayload,
): Promise<SubmitQuestionnaireResult> => {
  try {
    const { data } = await request.post<SubmitQuestionnaireApiResponse>(
      '/assessment-requests/questionnaire',
      payload,
    );

    return {
      status: RequestStatus.SUCCESS,
      data,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data;
      return {
        status: RequestStatus.FAILURE,
        error: responseData?.message || error.message,
        errorCode: errorCodeFromResponseBody(responseData),
      };
    }
    return {
      status: RequestStatus.FAILURE,
      error: String(error),
      errorCode: undefined,
    };
  }
};

export const createAssessmentRequest = async (
  payload: AssessmentRequestPayload,
): Promise<CreateAssessmentRequestResult> => {
  try {
    const { status, data } = await request.post(
      '/assessment-requests',
      payload,
    );

    if (status >= 200 && status < 300) {
      return {
        status: RequestStatus.SUCCESS,
        data,
      };
    }

    return {
      status: RequestStatus.FAILURE,
      error: 'Submit assessment failed',
      errorCode: errorCodeFromResponseBody(data),
      httpStatus: status,
      networkError: false,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data;
      return {
        status: RequestStatus.FAILURE,
        error: responseData?.message || error.message,
        errorCode: errorCodeFromResponseBody(responseData),
        httpStatus: error.response?.status,
        networkError: !error.response,
      };
    }
    return {
      status: RequestStatus.FAILURE,
      error: String(error),
      errorCode: undefined,
      httpStatus: undefined,
      networkError: false,
    };
  }
};
